import React, { useState, useEffect } from "react";
import { getTimekeepingData } from "../services/azureTimekeepingService";

const HistorischeAuswertung = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("person");
  const [timeAggregation, setTimeAggregation] = useState("kw");
  const [selectedProject, setSelectedProject] = useState("alle"); // Neu
  const [produktivMode, setProduktivMode] = useState(false); // Neu
  const [rawData, setRawData] = useState([]);

  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-01-01`;
  });
  const [dateTo, setDateTo] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-12-31`;
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (rawData.length > 0) {
      const processed = processDataForChart(rawData);
      setData(processed);
    }
  }, [timeAggregation]);

  const loadData = async () => {
    setLoading(true);
    try {
      const timekeepingData = await getTimekeepingData(dateFrom, dateTo);
      setRawData(timekeepingData); // Rohdaten speichern
      const processed = processDataForChart(timekeepingData);
      setData(processed);
    } catch (error) {
      console.error("Fehler beim Laden:", error);
    } finally {
      setLoading(false);
    }
  };

  const getQuarter = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth();
    const quarter = Math.floor(month / 3) + 1;
    const year = date.getFullYear();
    return `Q${quarter} ${year}`;
  };

  const getYear = (dateString) => {
    const date = new Date(dateString);
    return date.getFullYear().toString();
  };

  const getTimeKey = (dateString) => {
    switch (timeAggregation) {
      case "quartal":
        return getQuarter(dateString);
      case "jahr":
        return getYear(dateString);
      case "kw":
      default:
        const weekNumber = getWeekNumber(dateString);
        const year = new Date(dateString).getFullYear();
        return `KW${String(weekNumber).padStart(2, "0")} ${year}`;
    }
  };

  const getWeekNumber = (dateString) => {
    const date = new Date(dateString);
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const getUniqueProjects = () => {
    const projects = new Set();
    Object.values(data).forEach((employee) => {
      Object.values(employee).forEach((timeData) => {
        Object.keys(timeData).forEach((category) => {
          // Nur "Arbeit"-Kategorien (= Projekte)
          if (
            category !== "Admin" &&
            category !== "Urlaub" &&
            category !== "Krank" &&
            category !== "Feiertag" &&
            category !== "Pause" &&
            category !== "Arbeit"
          ) {
            projects.add(category);
          }
        });
      });
    });
    return Array.from(projects).sort();
  };

  const getProduktivData = () => {
    const produktivData = {};

    Object.entries(data).forEach(([employeeName, employeeData]) => {
      produktivData[employeeName] = {};

      Object.entries(employeeData).forEach(([timeKey, categories]) => {
        produktivData[employeeName][timeKey] = {
          Arbeit: 0,
          Rest: 0,
        };

        Object.entries(categories).forEach(([category, hours]) => {
          if (
            category === "Admin" ||
            category === "Urlaub" ||
            category === "Krank" ||
            category === "Feiertag"
          ) {
            produktivData[employeeName][timeKey]["Rest"] += hours;
          } else {
            produktivData[employeeName][timeKey]["Arbeit"] += hours;
          }
        });
      });
    });

    return produktivData;
  };

  const getAllTimeKeys = () => {
    const allKeys = new Set();

    Object.values(data).forEach((employeeData) => {
      Object.keys(employeeData).forEach((timeKey) => {
        allKeys.add(timeKey);
      });
    });

    // Sortieren
    return Array.from(allKeys).sort((a, b) => {
      if (timeAggregation === "jahr") {
        return parseInt(a) - parseInt(b);
      } else if (timeAggregation === "quartal") {
        const [qA, yearA] = a.split(" ");
        const [qB, yearB] = b.split(" ");
        if (yearA !== yearB) {
          return parseInt(yearA) - parseInt(yearB);
        }
        return parseInt(qA.replace("Q", "")) - parseInt(qB.replace("Q", ""));
      } else {
        const [kwA, yearA] = a.split(" ");
        const [kwB, yearB] = b.split(" ");
        const weekNumA = parseInt(kwA.replace("KW", ""));
        const weekNumB = parseInt(kwB.replace("KW", ""));
        if (yearA !== yearB) {
          return parseInt(yearA) - parseInt(yearB);
        }
        return weekNumA - weekNumB;
      }
    });
  };

  const getProjectFilteredData = (projectName) => {
    const projectData = {};

    Object.entries(data).forEach(([employeeName, employeeData]) => {
      Object.entries(employeeData).forEach(([timeKey, categories]) => {
        if (!projectData[timeKey]) {
          projectData[timeKey] = {};
        }

        const hours = categories[projectName] || 0;
        if (hours > 0) {
          projectData[timeKey][employeeName] = hours;
        }
      });
    });

    return projectData;
  };

  const processDataForChart = (timekeepingData) => {
    const employeeData = {};

    timekeepingData.forEach((entry) => {
      const timeKey = getTimeKey(entry.date);
      const totalHours = entry.durationHours + entry.durationMinutes / 60;

      if (!employeeData[entry.employeeName]) {
        employeeData[entry.employeeName] = {};
      }

      if (!employeeData[entry.employeeName][timeKey]) {
        employeeData[entry.employeeName][timeKey] = {};
      }

      let category = entry.typeName;

      if (entry.typeName === "Arbeit") {
        let contractName = entry.contractNoteName || "";

        if (contractName.length === 4 && /^\d{4}$/.test(contractName)) {
          contractName = "0014 - Gleispool - Vorprojekt";
        }

        if (contractName) {
          category = contractName;
        }
      }

      if (!employeeData[entry.employeeName][timeKey][category]) {
        employeeData[entry.employeeName][timeKey][category] = 0;
      }

      employeeData[entry.employeeName][timeKey][category] += totalHours;
    });

    return employeeData;
  };

  
  const getAggregatedData = () => {
    const aggregated = {};

    Object.values(data).forEach((employeeData) => {
      Object.entries(employeeData).forEach(([week, categories]) => {
        if (!aggregated[week]) {
          aggregated[week] = {};
        }

        Object.entries(categories).forEach(([category, hours]) => {
          if (!aggregated[week][category]) {
            aggregated[week][category] = 0;
          }
          aggregated[week][category] += hours;
        });
      });
    });

    return aggregated;
  };

  const getUniqueCategories = () => {
    const categories = new Set();
    Object.values(data).forEach((employee) => {
      Object.values(employee).forEach((week) => {
        Object.keys(week).forEach((category) => categories.add(category));
      });
    });
    return Array.from(categories).sort();
  };

  const getCategoryColor = (category, index) => {
    const colors = [
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
      "#f59e0b",
      "#10b981",
      "#06b6d4",
      "#6366f1",
      "#84cc16",
      "#f97316",
      "#14b8a6",
      "#a855f7",
      "#eab308",
    ];

    if (category.includes("Admin")) return "#64748b";
    if (category.includes("Urlaub")) return "#22c55e";
    if (category.includes("Krank")) return "#ef4444";
    if (category.includes("Feiertag")) return "#a855f7";

    return colors[index % colors.length];
  };

  const renderBarChart = (
    employeeName,
    weekData,
    allCategories,
    allTimeKeys
  ) => {
    const maxHours = Math.max(
      ...allTimeKeys.map((timeKey) => {
        const timeData = weekData[timeKey] || {};
        return Object.values(timeData).reduce((sum, hours) => sum + hours, 0);
      })
    );

    const chartHeight = 300;
    const barWidth = 60;
    const gap = 10;

    return (
      <div key={employeeName} style={{ marginBottom: "20px" }}>
        <h3
          style={{
            marginBottom: "10px",
            color: "var(--text-color)",
            paddingLeft: "20px",
          }}
        >
          {employeeName}
        </h3>

        <div
          style={{
            padding: "20px",
            background: "var(--input-bg)",
            borderRadius: "8px",
          }}
        >
          <svg
            width={allTimeKeys.length * (barWidth + gap) + 100}
            height={chartHeight + 80}
            style={{ display: "block" }}
          >
            {allTimeKeys.map((timeKey, timeIndex) => {
              const x = timeIndex * (barWidth + gap) + 50;
              let currentY = chartHeight;
              const timeData = weekData[timeKey] || {};
              const timeTotal = Object.values(timeData).reduce(
                (sum, h) => sum + h,
                0
              );

              return (
                <g key={timeKey}>
                  {allCategories.map((category, catIndex) => {
                    const hours = timeData[category] || 0;
                    if (hours === 0) return null;

                    const barHeight = (hours / maxHours) * (chartHeight - 40);
                    currentY -= barHeight;

                    return (
                      <g key={category}>
                        <rect
                          x={x}
                          y={currentY}
                          width={barWidth}
                          height={barHeight}
                          fill={getCategoryColor(category, catIndex)}
                          opacity={0.9}
                          style={{ cursor: "pointer" }}
                        >
                          <title>{`${category}\n${hours.toFixed(1)}h (${(
                            hours * 60
                          ).toFixed(0)} Min)`}</title>
                        </rect>
                        {barHeight > 20 && (
                          <text
                            x={x + barWidth / 2}
                            y={currentY + barHeight / 2}
                            textAnchor="middle"
                            fill="white"
                            fontSize="11"
                            fontWeight="bold"
                            pointerEvents="none"
                          >
                            {hours.toFixed(1)}
                          </text>
                        )}
                      </g>
                    );
                  })}

                  <text
                    x={x + barWidth / 2}
                    y={chartHeight + 20}
                    textAnchor="middle"
                    fill="var(--text-color)"
                    fontSize="12"
                  >
                    {timeKey}
                  </text>

                  {timeTotal > 0 && (
                    <text
                      x={x + barWidth / 2}
                      y={chartHeight + 35}
                      textAnchor="middle"
                      fill="var(--text-bg-color)"
                      fontSize="11"
                      fontWeight="bold"
                    >
                      {timeTotal.toFixed(1)}h
                    </text>
                  )}
                </g>
              );
            })}

            <line
              x1={40}
              y1={chartHeight}
              x2={allTimeKeys.length * (barWidth + gap) + 60}
              y2={chartHeight}
              stroke="var(--text-bg-color)"
              strokeWidth={1}
            />
          </svg>
        </div>
      </div>
    );
  };

  const renderProjectBarChart = (projectName, projectData, allTimeKeys) => {
    const allEmployees = Array.from(
      new Set(
        Object.values(projectData).flatMap((timeData) => Object.keys(timeData))
      )
    ).sort();

    const maxHours = Math.max(
      ...allTimeKeys.map((timeKey) => {
        const timeData = projectData[timeKey] || {};
        return Object.values(timeData).reduce((sum, hours) => sum + hours, 0);
      })
    );

    const chartHeight = 300;
    const barWidth = 60;
    const gap = 10;

    const employeeColors = [
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
      "#f59e0b",
      "#10b981",
      "#06b6d4",
      "#6366f1",
      "#84cc16",
      "#f97316",
      "#14b8a6",
    ];

    return (
      <div style={{ marginBottom: "20px" }}>
        <h3
          style={{
            marginBottom: "10px",
            color: "var(--text-color)",
            paddingLeft: "20px",
          }}
        >
          {projectName}
        </h3>

        <div
          style={{
            padding: "20px",
            background: "var(--card-bg)",
            borderRadius: "8px",
          }}
        >
          <svg
            width={allTimeKeys.length * (barWidth + gap) + 100}
            height={chartHeight + 80}
            style={{ display: "block" }}
          >
            {allTimeKeys.map((timeKey, timeIndex) => {
              const x = timeIndex * (barWidth + gap) + 50;
              let currentY = chartHeight;
              const timeData = projectData[timeKey] || {};
              const timeTotal = Object.values(timeData).reduce(
                (sum, h) => sum + h,
                0
              );

              return (
                <g key={timeKey}>
                  {allEmployees.map((employee, empIndex) => {
                    const hours = timeData[employee] || 0;
                    if (hours === 0) return null;

                    const barHeight = (hours / maxHours) * (chartHeight - 40);
                    currentY -= barHeight;

                    return (
                      <g key={employee}>
                        <rect
                          x={x}
                          y={currentY}
                          width={barWidth}
                          height={barHeight}
                          fill={
                            employeeColors[empIndex % employeeColors.length]
                          }
                          opacity={0.9}
                          style={{ cursor: "pointer" }}
                        >
                          <title>{`${employee}\n${hours.toFixed(1)}h (${(
                            hours * 60
                          ).toFixed(0)} Min)`}</title>
                        </rect>
                        {barHeight > 20 && (
                          <text
                            x={x + barWidth / 2}
                            y={currentY + barHeight / 2}
                            textAnchor="middle"
                            fill="white"
                            fontSize="11"
                            fontWeight="bold"
                            pointerEvents="none"
                          >
                            {hours.toFixed(1)}
                          </text>
                        )}
                      </g>
                    );
                  })}

                  <text
                    x={x + barWidth / 2}
                    y={chartHeight + 20}
                    textAnchor="middle"
                    fill="var(--text-color)"
                    fontSize="12"
                  >
                    {timeKey}
                  </text>

                  {timeTotal > 0 && (
                    <text
                      x={x + barWidth / 2}
                      y={chartHeight + 35}
                      textAnchor="middle"
                      fill="var(--text-bg-color)"
                      fontSize="11"
                      fontWeight="bold"
                    >
                      {timeTotal.toFixed(1)}h
                    </text>
                  )}
                </g>
              );
            })}

            <line
              x1={40}
              y1={chartHeight}
              x2={allTimeKeys.length * (barWidth + gap) + 60}
              y2={chartHeight}
              stroke="var(--text-bg-color)"
              strokeWidth={1}
            />
          </svg>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "15px",
              marginTop: "20px",
              justifyContent: "center",
            }}
          >
            {allEmployees.map((employee, index) => (
              <div
                key={employee}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    background: employeeColors[index % employeeColors.length],
                    borderRadius: "3px",
                  }}
                />
                <span style={{ color: "var(--text-color)" }}>{employee}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "30px",
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              color: "var(--text-color)",
              fontSize: "14px",
            }}
          >
            Ansicht:
          </label>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              background: "var(--input-bg)",
              color: "var(--text-color)",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            <option value="person">Nach Person</option>
            <option value="gesamt">Gesamt</option>
          </select>
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              color: "var(--text-color)",
              fontSize: "14px",
            }}
          >
            Zeitraum:
          </label>
          <select
            value={timeAggregation}
            onChange={(e) => {
              setTimeAggregation(e.target.value);
              // setTimeout entfernen - useEffect übernimmt das
            }}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              background: "var(--card-bg)",
              color: "var(--text-color)",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            <option value="kw">KW</option>
            <option value="quartal">Quartal</option>
            <option value="jahr">Jahr</option>
          </select>
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              color: "var(--text-color)",
              fontSize: "14px",
            }}
          >
            Projekt:
          </label>
          <select
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              setProduktivMode(false);
            }}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              background: "var(--card-bg)",
              color: "var(--text-color)",
              fontSize: "14px",
              cursor: "pointer",
              minWidth: "200px",
            }}
          >
            <option value="alle">Alle Projekte</option>
            {getUniqueProjects().map((project) => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </select>
        </div>

        {selectedProject === "alle" && (
          <button
            onClick={() => setProduktivMode(!produktivMode)}
            className={produktivMode ? "btn btn-primary" : "btn btn-secondary"}
            style={{ padding: "8px 16px", marginTop: "23px" }}
          >
            {produktivMode ? "Produktiv ✓" : "Produktiv"}
          </button>
        )}

        <div>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              color: "var(--text-color)",
              fontSize: "14px",
            }}
          >
            Von:
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              background: "var(--card-bg)",
              color: "var(--text-color)",
            }}
          />
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              color: "var(--text-color)",
              fontSize: "14px",
            }}
          >
            Bis:
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              background: "var(--card-bg)",
              color: "var(--text-color)",
            }}
          />
        </div>

        <button
          onClick={loadData}
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? "Lädt..." : "Aktualisieren"}
        </button>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => {
              const year = new Date().getFullYear();
              setDateFrom(`${year}-01-01`);
              setDateTo(`${year}-12-31`);
            }}
            className="btn btn-secondary"
            style={{ padding: "8px 16px" }}
          >
            Dieses Jahr
          </button>

          <button
            onClick={() => {
              const year = new Date().getFullYear() - 1;
              setDateFrom(`${year}-01-01`);
              setDateTo(`${year}-12-31`);
            }}
            className="btn btn-secondary"
            style={{ padding: "8px 16px" }}
          >
            Letztes Jahr
          </button>

          <button
            onClick={() => {
              const date = new Date();
              date.setMonth(date.getMonth() - 3);
              setDateFrom(date.toISOString().split("T")[0]);
              setDateTo(new Date().toISOString().split("T")[0]);
            }}
            className="btn btn-secondary"
            style={{ padding: "8px 16px" }}
          >
            Letzte 3 Monate
          </button>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-color)",
          }}
        >
          Lade Daten...
        </div>
      ) : Object.keys(data).length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-color)",
          }}
        >
          Keine Daten vorhanden
        </div>
      ) : (
        <>
          {/* Gemeinsamer Scroll-Container */}
          <div
            style={{
              overflowX: "auto",
              border: "1px solid var(--border-color)",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          >
            {selectedProject !== "alle"
              ? // Projekt-Ansicht
                (() => {
                  const allTimeKeys = getAllTimeKeys();
                  return renderProjectBarChart(
                    selectedProject,
                    getProjectFilteredData(selectedProject),
                    allTimeKeys
                  );
                })()
              : // Standard-Ansicht
                (() => {
                  const dataToUse = produktivMode ? getProduktivData() : data;
                  const allCategories = produktivMode
                    ? ["Arbeit", "Rest"]
                    : getUniqueCategories();
                  const allTimeKeys = getAllTimeKeys();

                  return viewMode === "person"
                    ? Object.entries(dataToUse).map(
                        ([employeeName, weekData]) =>
                          renderBarChart(
                            employeeName,
                            weekData,
                            allCategories,
                            allTimeKeys
                          )
                      )
                    : renderBarChart(
                        "Gesamt (alle Personen)",
                        produktivMode
                          ? (() => {
                              const aggregated = {};
                              Object.values(dataToUse).forEach(
                                (employeeData) => {
                                  Object.entries(employeeData).forEach(
                                    ([week, categories]) => {
                                      if (!aggregated[week]) {
                                        aggregated[week] = {};
                                      }
                                      Object.entries(categories).forEach(
                                        ([category, hours]) => {
                                          if (!aggregated[week][category]) {
                                            aggregated[week][category] = 0;
                                          }
                                          aggregated[week][category] += hours;
                                        }
                                      );
                                    }
                                  );
                                }
                              );
                              return aggregated;
                            })()
                          : getAggregatedData(),
                        allCategories,
                        allTimeKeys
                      );
                })()}
          </div>

          {selectedProject === "alle" && (
            <div
              style={{
                marginTop: "20px",
                padding: "20px",
                background: "var(--card-bg)",
                borderRadius: "8px",
                borderTop: "2px solid var(--border-color)",
              }}
            >
              <h4
                style={{
                  marginBottom: "15px",
                  color: "var(--text-color)",
                  textAlign: "center",
                }}
              >
                Legende
              </h4>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "15px",
                  justifyContent: "center",
                }}
              >
                {(produktivMode
                  ? ["Arbeit", "Rest"]
                  : getUniqueCategories()
                ).map((category, index) => (
                  <div
                    key={category}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        background: produktivMode
                          ? category === "Arbeit"
                            ? "#10b981"
                            : "#64748b"
                          : getCategoryColor(category, index),
                        borderRadius: "3px",
                      }}
                    />
                    <span style={{ color: "var(--text-color)" }}>
                      {category}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HistorischeAuswertung;
